import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

document.addEventListener("DOMContentLoaded", () => {
  const slides = gsap.utils.toArray(".banner-content");
  const shapes = gsap.utils.toArray(".floating-shapes .shape");
  const banner = document.querySelector(".section.banner");
  if (!banner || !slides.length) return;

  const header = document.querySelector("header");
  const headerHeight = header ? header.offsetHeight : 0;
  let currentIndex = 0;
  let isAnimating = false;
  const lastIndex = slides.length - 1;
  const oscillationTweens = [];
  let parallaxActive = false;

  const getShapePositions = () => {
    const width = window.innerWidth;
    const height = window.innerHeight;

    if (width <= 768) {
      const shapeSize = Math.max(56, Math.min(86, Math.round(width * 0.18)));
      const yBase = Math.max(190, Math.min(340, Math.round(height * 0.35)));

      return [
        [
          { x: Math.round(width * 0.62), y: 12 },
          { x: Math.round(width * 0.78), y: 84 },
          { x: Math.round(width * 0.12), y: yBase - 16 }
        ],
        [
          { x: Math.round(width * 0.64), y: 18 },
          { x: Math.round(width * 0.82), y: 104 },
          { x: Math.round(width * 0.24), y: yBase + 12 }
        ],
        [
          { x: Math.round(width * 0.7), y: 24 },
          { x: Math.round(width * 0.82), y: 136 },
          { x: Math.round(width * 0.24), y: yBase + 42 }
        ],
        [
          { x: Math.round(width * 0.06), y: 18 },
          { x: Math.round(width * 0.28), y: 54 },
          { x: Math.round(width * 0.18), y: yBase - 34 }
        ]
      ].map((slide) =>
        slide.map((pos) => ({
          x: Math.max(8, Math.min(pos.x, width - shapeSize - 8)),
          y: Math.max(-20, Math.min(pos.y, yBase + 70)),
        }))
      );
    }

    if (width <= 1200) {
      return [
        [{ x: 180, y: 10 }, { x: 430, y: 80 }, { x: 130, y: 130 }],
        [{ x: 280, y: 16 }, { x: 560, y: 170 }, { x: 230, y: 220 }],
        [{ x: 330, y: 26 }, { x: 560, y: 250 }, { x: 250, y: 290 }],
        [{ x: 34, y: 10 }, { x: 260, y: 16 }, { x: 160, y: 190 }]
      ];
    }

    return [
      [{ x: 750, y: -50 }, { x: 1120, y: 110 }, { x: 290, y: 150 }],
      [{ x: 1050, y: -30 }, { x: 1450, y: 370 }, { x: 350, y: 210 }],
      [{ x: 1450, y: -40 }, { x: 1450, y: 420 }, { x: 350, y: 420 }],
      [{ x: 50, y: 10 }, { x: 620, y: 10 }, { x: 450, y: 260 }]
    ];
  };

  let shapePositions = getShapePositions();
  const getPositionsForIndex = (index) => shapePositions[Math.min(index, shapePositions.length - 1)];
  const slideInDirections = ["top", "bottom", "left", "right"];

  // === INIT SLIDES ===
  slides.forEach((slide, i) => {
    gsap.set(slide, {
      x: 0,
      y: 0,
      opacity: i === 0 ? 1 : 0,
      pointerEvents: i === 0 ? "auto" : "none",
      zIndex: i === 0 ? 2 : 1,
      visibility: i === 0 ? "visible" : "hidden",
    });

    const title = slide.querySelector(".main-title h1");
    const desc = slide.querySelector(".banner-desc p");

    if (i !== 0) {
      if (title) gsap.set(title, { x: 0, y: 0, opacity: 0 });
      if (desc) gsap.set(desc, { x: 0, y: 0, opacity: 0 });
    }
  });

  // === FADE IN FIRST SLIDE ===
  const firstTitle = slides[0].querySelector(".main-title h1");
  const firstDesc = slides[0].querySelector(".banner-desc p");
  gsap.fromTo(
    [firstTitle, firstDesc],
    { opacity: 0, y: 50 },
    { opacity: 1, y: 0, duration: 4, ease: "power2.out", stagger: 0.2 }
  );

  // === MAIN SLIDE FUNCTION ===
  const showSlide = (newIndex, forward = true) => {
    if (isAnimating || newIndex === currentIndex || newIndex < 0 || newIndex > lastIndex)
      return;
    isAnimating = true;

    oscillationTweens.forEach((t) => t.pause());

    const currentSlide = slides[currentIndex];
    const nextSlide = slides[newIndex];

    const currentTitle = currentSlide.querySelector(".main-title h1");
    const currentDesc = currentSlide.querySelector(".banner-desc p");
    const nextTitle = nextSlide.querySelector(".main-title h1");
    const nextDesc = nextSlide.querySelector(".banner-desc p");

    const viewportHeight = window.innerHeight;
    const slideOutY = -viewportHeight + headerHeight;
    let slideInX = 0, slideInY = 0;
    const dir = slideInDirections[newIndex % slideInDirections.length];

    switch (dir) {
      case "top": slideInY = -viewportHeight; break;
      case "bottom": slideInY = viewportHeight; break;
      case "left": slideInX = -window.innerWidth; break;
      case "right": slideInX = window.innerWidth; break;
    }

    // For last slide (index 3), keep it fixed without Y movement
    if (newIndex === 3) {
      slideInY = 0;
    }

    // Prepare next slide
    gsap.set(nextSlide, {
      opacity: 0,
      pointerEvents: "auto",
      zIndex: 3,
      visibility: "visible",
    });
    if (nextTitle) gsap.set(nextTitle, { x: slideInX, y: slideInY, opacity: 0 });
    if (nextDesc) gsap.set(nextDesc, { x: slideInX, y: slideInY, opacity: 0 });

    const tl = gsap.timeline({
      onComplete: () => {
        gsap.set(currentSlide, {
          opacity: 0,
          pointerEvents: "none",
          zIndex: 1,
          visibility: "hidden",
        });
        gsap.set(nextSlide, {
          opacity: 1,
          pointerEvents: "auto",
          zIndex: 2,
          visibility: "visible",
        });
        currentIndex = newIndex;

        // Restart floating if NOT on fourth slide
        if (newIndex !== 3) {
          oscillationTweens.forEach((t) => t.kill());
          oscillationTweens.length = 0;
          shapes.forEach((shape, i) => {
            const osc = gsap.to(shape, {
              x: "+=15",
              y: "+=20",
              duration: 4 + i,
              repeat: -1,
              yoyo: true,
              ease: "sine.inOut",
            });
            oscillationTweens.push(osc);
          });
        } else {
          oscillationTweens.forEach((t) => t.kill());
        }

        isAnimating = false;
      },
    });

    // === CURRENT SLIDE OUT ===
    tl.to(currentSlide, { opacity: 0, duration: 1.5, ease: "power2.inOut" }, 0);
    if (currentTitle)
      tl.to(currentTitle, { y: slideOutY, opacity: 0, duration: 1.5, ease: "power2.inOut" }, 0);
    if (currentDesc)
      tl.to(currentDesc, { y: slideOutY, opacity: 0, duration: 1.5, ease: "power2.inOut" }, 0);

    // === NEXT SLIDE IN ===
    tl.to(nextSlide, { opacity: 1, duration: 1.5, ease: "power2.inOut" }, 0.2);
    if (nextTitle)
      tl.to(nextTitle, { x: 0, y: 0, opacity: 1, duration: 1.5, ease: "power2.out" }, 0.2);
    if (nextDesc)
      tl.to(nextDesc, { x: 0, y: 0, opacity: 1, duration: 1.5, ease: "power2.out" }, 0.25);

    // === SHAPE ANIMATION ===
    if (newIndex === 3) {
      // 🔥 Fourth slide: shapes slide out and hide
      const rotations = [-90, -45, 0];
      shapes.forEach((shape, i) => {
        tl.to(
          shape,
          {
            rotate: rotations[i] || 0,
            opacity: 0,
            y: "+=200",
            duration: 1.8,
            ease: "power2.inOut",
            onComplete: () => gsap.set(shape, { visibility: "hidden" }),
          },
          0
        );
      });
    } else {
      // All other slides
      const newPositions = getPositionsForIndex(newIndex);
      shapes.forEach((shape, i) => {
        gsap.set(shape, { visibility: "visible" });
        const shapeAnim = {
          x: newPositions[i].x,
          y: newPositions[i].y,
          opacity: 1,
          rotate: 0,
          duration: 1.5,
          ease: "power2.inOut",
        };

        // Slide 3 rotation
        if (newIndex === 2) {
          shapeAnim.rotate = -20;
          shapeAnim.duration = 2;
        }

        tl.to(shape, shapeAnim, 0);
      });
    }
  };

  // === WHEEL SCROLL NAVIGATION ===
  window.addEventListener(
    "wheel",
    (e) => {
      // Handle scroll up when parallax is active and at top
      if (parallaxActive && window.scrollY === 0 && e.deltaY < 0) {
        e.preventDefault();
        
        // Smooth transition back to slides - reset to first slide
        gsap.to(banner, {
          opacity: 1,
          duration: 0.4,
          ease: 'power2.inOut',
          onComplete: () => {
            disableParallax();
            setTimeout(() => {
              showSlide(0, false);
            }, 100);
          }
        });
        return;
      }
      
      if (parallaxActive) return; // Let ScrollTrigger handle scroll
      
      const rect = banner.getBoundingClientRect();
      if (window.scrollY !== 0 || !(rect.top < window.innerHeight && rect.bottom > 0)) return;

      if (e.deltaY > 0 && currentIndex < lastIndex) {
        e.preventDefault();
        showSlide(currentIndex + 1, true);
      } else if (e.deltaY < 0 && currentIndex > 0) {
        e.preventDefault();
        showSlide(currentIndex - 1, false);
      } else if (e.deltaY > 0 && currentIndex === lastIndex && !parallaxActive) {
        // Enable parallax on last slide
        e.preventDefault();
        enableParallax();
      }
    },
    { passive: false }
  );

  // === TOUCH SWIPE NAVIGATION ===
  let touchStartY = 0;
  let touchEndY = 0;
  let touchStartX = 0;
  const minSwipe = 40;
  let touchActive = false;

  window.addEventListener(
    "touchstart",
    (e) => {
      if (!e.touches || !e.touches.length) return;
      
      const rect = banner.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
      
      // Capture touch if banner is visible and at top of page
      if (window.scrollY === 0 && isVisible) {
        touchStartY = e.touches[0].clientY;
        touchStartX = e.touches[0].clientX;
        touchActive = true;
      } else if (parallaxActive && window.scrollY === 0) {
        touchStartY = e.touches[0].clientY;
        touchActive = true;
      }
    },
    { passive: true }
  );

  window.addEventListener(
    "touchmove",
    (e) => {
      if (!e.touches || !e.touches.length || !touchActive) return;
      
      const rect = banner.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
      
      // When banner is visible and at top, prevent ALL scroll
      if (window.scrollY === 0 && isVisible && !parallaxActive) {
        const touchCurrentY = e.touches[0].clientY;
        const touchCurrentX = e.touches[0].clientX;
        const deltaY = Math.abs(touchStartY - touchCurrentY);
        const deltaX = Math.abs(touchStartX - touchCurrentX);
        
        // If vertical movement is dominant over horizontal, prevent scroll
        if (deltaY > 5 && deltaY > deltaX) {
          e.preventDefault();
        }
      } else if (parallaxActive && window.scrollY === 0) {
        // In parallax mode at top, check if swiping down to go back
        const touchCurrentY = e.touches[0].clientY;
        const deltaY = touchCurrentY - touchStartY;
        
        // If swiping down, prevent scroll to handle going back
        if (deltaY > 5) {
          e.preventDefault();
        }
      }
    },
    { passive: false }
  );

  window.addEventListener(
    "touchend",
    (e) => {
      if (!e.changedTouches || !e.changedTouches.length || !touchActive) {
        touchActive = false;
        touchStartY = 0;
        return;
      }
      
      touchEndY = e.changedTouches[0].clientY;
      const deltaY = touchStartY - touchEndY;

      // Handle swipe down when parallax is active and at top
      if (parallaxActive && window.scrollY === 0 && deltaY < -minSwipe) {
        // Smooth transition back to slides - reset to first slide
        gsap.to(banner, {
          opacity: 1,
          duration: 0.4,
          ease: 'power2.inOut',
          onComplete: () => {
            disableParallax();
            setTimeout(() => {
              showSlide(0, false);
            }, 100);
          }
        });
        
        touchActive = false;
        touchStartY = 0;
        return;
      }

      if (parallaxActive) {
        touchActive = false;
        touchStartY = 0;
        return;
      }

      if (Math.abs(deltaY) < minSwipe || isAnimating) {
        touchActive = false;
        touchStartY = 0;
        return;
      }

      const rect = banner.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
      
      if (window.scrollY === 0 && isVisible) {
        if (deltaY > 0 && currentIndex < lastIndex) {
          showSlide(currentIndex + 1, true);
        } else if (deltaY < 0 && currentIndex > 0) {
          showSlide(currentIndex - 1, false);
        } else if (deltaY > 0 && currentIndex === lastIndex && !parallaxActive) {
          // Enable parallax on last slide
          enableParallax();
        }
      }
      
      touchActive = false;
      touchStartY = 0;
    },
    { passive: false }
  );

  // === ENABLE PARALLAX SCROLL ===
  let scrollTriggerInstance = null;
  let portfolioSection = null;
  
  function enableParallax() {
    parallaxActive = true;
    
    // Animate logo and toggle - slide up and fade out
    const logo = header ? header.querySelector('.logo') : null;
    const toggle = header ? header.querySelector('.toggle') : null;
    
    if (logo) {
      gsap.to(logo, {
        opacity: 0,
        y: -20,
        duration: 0.6,
        ease: 'power2.inOut'
      });
    }
    
    if (toggle) {
      gsap.to(toggle, {
        opacity: 0,
        y: -20,
        duration: 0.6,
        ease: 'power2.inOut'
      });
    }
    
    // Get current banner position before fixing
    const bannerRect = banner.getBoundingClientRect();
    const currentTop = bannerRect.top;
    
    // Lock banner exactly where it is
    banner.style.position = 'fixed';
    banner.style.top = currentTop + 'px';
    banner.style.left = '0';
    banner.style.width = '100%';
    banner.style.zIndex = '1';
    
    // Lock all banner internal elements
    const bannerSlides = banner.querySelector('.banner-slides');
    const currentSlide = slides[currentIndex];
    
    if (bannerSlides) {
      const slidesRect = bannerSlides.getBoundingClientRect();
      gsap.set(bannerSlides, {
        x: 0,
        y: 0,
        clearProps: 'transform'
      });
    }
    
    // Lock current slide content
    if (currentSlide) {
      const title = currentSlide.querySelector('.main-title h1');
      const desc = currentSlide.querySelector('.banner-desc p');
      
      if (title) {
        gsap.set(title, {
          x: 0,
          y: 0,
          clearProps: 'transform'
        });
      }
      if (desc) {
        gsap.set(desc, {
          x: 0,
          y: 0,
          clearProps: 'transform'
        });
      }
    }
    
    // Enable scroll and touch
    document.body.style.overflow = 'auto';
    banner.style.touchAction = 'auto'; // Allow scroll in parallax mode
    
    // Get next section (portfolio)
    portfolioSection = banner.nextElementSibling;
    
    if (portfolioSection) {
      // Initially position portfolio below viewport
      portfolioSection.style.position = 'relative';
      portfolioSection.style.zIndex = '2';
      portfolioSection.style.marginTop = '100vh';
      
      // Create scroll trigger to fade out banner as portfolio covers it
      scrollTriggerInstance = ScrollTrigger.create({
        trigger: portfolioSection,
        start: "top bottom",
        end: "top top",
        scrub: true,
        onUpdate: (self) => {
          // Fade out banner as portfolio scrolls over it
          gsap.set(banner, { 
            opacity: 1 - self.progress
          });
        }
      });
    }
  }
  
  function disableParallax() {
    parallaxActive = false;
    
    // Kill scroll trigger
    if (scrollTriggerInstance) {
      scrollTriggerInstance.kill();
      scrollTriggerInstance = null;
    }
    
    // Restore logo and toggle with slide down animation
    const logo = header ? header.querySelector('.logo') : null;
    const toggle = header ? header.querySelector('.toggle') : null;
    
    if (logo) {
      gsap.to(logo, {
        opacity: 1,
        y: 0,
        duration: 0.5,
        ease: 'power2.out'
      });
    }
    
    if (toggle) {
      gsap.to(toggle, {
        opacity: 1,
        y: 0,
        duration: 0.5,
        ease: 'power2.out'
      });
    }
    
    // Smoothly reset banner styles
    gsap.set(banner, {
      position: 'relative',
      top: 0,
      opacity: 1
    });
    
    // Reset portfolio with smooth transition
    if (portfolioSection) {
      gsap.to(portfolioSection, {
        marginTop: 0,
        duration: 0.3,
        ease: 'power2.inOut'
      });
    }
    
    // Disable scroll and touch
    document.body.style.overflow = 'hidden';
    banner.style.touchAction = 'none'; // Prevent scroll during slides
    window.scrollTo(0, 0);
  }

  // === INITIAL FLOATING SHAPES ===
  const bannerRect = banner.getBoundingClientRect();
  const initialSettings = [
    {
      from: { x: -160, y: 30, opacity: 0, visibility: "hidden" },
      to: { ...shapePositions[0][0], opacity: 1, visibility: "visible" },
    },
    {
      from: { x: bannerRect.width + 140, y: 70, opacity: 0, visibility: "hidden" },
      to: { ...shapePositions[0][1], opacity: 1, visibility: "visible" },
    },
    {
      from: { x: bannerRect.width / 8, y: -120, opacity: 0, visibility: "hidden" },
      to: { ...shapePositions[0][2], opacity: 1, visibility: "visible" },
    },
  ];

  shapes.forEach((shape, i) => {
    gsap.set(shape, initialSettings[i].from);
    gsap.to(shape, {
      ...initialSettings[i].to,
      duration: 1.5,
      ease: "power3.out",
      delay: i * 0.3,
    });
    const osc = gsap.to(shape, {
      x: "+=15",
      y: "+=20",
      duration: 4 + i,
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut",
      delay: 1.5,
    });
    oscillationTweens.push(osc);
  });

  window.addEventListener("resize", () => {
    shapePositions = getShapePositions();

    if (currentIndex !== 3) {
      const updatedPositions = getPositionsForIndex(currentIndex);
      shapes.forEach((shape, i) => {
        gsap.to(shape, {
          x: updatedPositions[i].x,
          y: updatedPositions[i].y,
          duration: 0.35,
          ease: "power1.out",
        });
      });
    }
  });
});
